import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkAssembledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for marking an order as assembled
 */
export interface MarkAssembledInput {
	orderId: string;
	assembly: {
		isComplete: boolean;
		dateAssembled?: number;
		agent?: string;
		notes?: string;
	};
}

/**
 * API function to mark an order as assembled
 */
export const markAssembled = async (
	servicesProviderId: string,
	{ orderId, assembly }: MarkAssembledInput,
): Promise<MarkAssembledResponse> => {
	const response = await apiClient.post<MarkAssembledResponse>(
		ordersEndpoints.markAssembled(servicesProviderId, orderId),
		{ assembly },
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) as assembled
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkAssembled = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<MarkAssembledInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark as assembled without an order ID or services provider ID.',
				);
			}
			return markAssembled(servicesProviderId, { orderId, assembly: input.assembly });
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_ASSEMBLED);
		},
	});
};
